/**
 * Global tunables. Single source of truth for anything a subsystem needs to
 * agree on. Nothing here reads from the DOM or from Three.js — import-safe
 * from tests and from workers.
 */

/** Internal playfield buffer. Everything in the sim uses these pixel units. */
export const VIEW = {
  /** Low-res buffer the scene renders into. Integer-scaled to the window. */
  width: 640,
  height: 360,
  /** Tile edge in buffer pixels. 640/16 = 40 cols, 360/16 = 22.5 rows. */
  tile: 16,
  cols: 40,
  rows: 22,
  /** Rows reserved at the bottom for the docked HUD strip. */
  hudRows: 0,
} as const;

/**
 * How far the *rendered* world may extend past the gameplay band.
 *
 * `VIEW` is the simulation. The track, the towers, the flock, the spatial hash
 * and every balance figure in `docs/BALANCE.md` are authored against 640x360,
 * and nothing here moves any of that. What this describes is the slice of world
 * the camera shows: on a window that is not 16:9 the region grows along the
 * short axis of the fit, with the 640x360 band centred inside it, so the extra
 * glass carries lattice instead of a black bar. See `core/region.ts` for the
 * rule and `Renderer.resize` for where it lands.
 */
export const REGION = {
  /**
   * Largest region extent as a multiple of the base axis.
   *
   * A fill budget, and the only number in this file that costs GPU time in
   * direct proportion to its value: the scene draw, the bloom pyramid, the
   * halation blur and the persistence feedback all run at region resolution.
   * Two more costs scale with it that the original list missed, one of them on
   * the CPU: `HydraBackground` cuts its quad to the region (a full-region
   * procedural fragment shader), and `WarpGrid` rebuilds at region extent —
   * measured at 2x, 984 nodes / 3,743 springs becomes 1,944 / 7,463, every
   * spring walked once per sim step, and ~362KB of vertex data re-uploaded per
   * frame against 181KB at base.
   * Only one axis ever extends — the other stays at its base — so the multiplier
   * *is* the area multiplier rather than its square root.
   *
   * What 2 costs, measured with `tools/perfprobe.mjs --scenario=swarm --auto=off`
   * on an M4 Max with vsync off, same window, region against region:
   *
   *   viewport    fill          median frame     cost p50      cost p95
   *   1456x420    1.00 -> 2.00   2.90 -> 6.30    1.50 -> 2.40  5.20 -> 8.50
   *   405x870     1.00 -> 1.96   3.90 -> 3.90    1.30 -> 1.50  4.10 -> 4.40
   *
   * One machine, one day, vsync off. A later re-run of the same probe measured
   * 1456x420 at 1.80 -> 2.00 median — direction agrees, magnitude does not
   * reproduce, so treat the table as a worst case observed rather than a
   * budget to size against.
   *
   * The two rows differ because the output box differs: the short-wide window
   * doubles the *canvas* as well as the buffer, the phone barely moves it. And
   * on a machine that is genuinely fill-bound the price is the whole price —
   * `tools/benchprobe.mjs --gl=swiftshader` at 1456x420 measures a quiet frame
   * at 15.0ms against 38.7ms and opens two ladder rungs lower, which is the boot
   * probe doing exactly its job without being told anything.
   *
   * Why 2 and not more: at every viewport in the responsive matrix the cap is
   * not the binding constraint. 405x870 asks for 1.96, 390x844 for 1.91,
   * 650x744 for 1.16, and all of them reach 98% of the box the layout gives the
   * playfield. Only 1456x420 touches the ceiling, and it touches it by eight
   * pixels. The cap is a guard against shapes past the matrix — a 5:1 letterbox,
   * a 1:3 portrait strip — where the fill would run away and the glass gained
   * per millisecond spent falls off a cliff. Beyond it the fit letterboxes as it
   * always did.
   */
  maxAxisMul: 2,
  /**
   * Region extents snap to this many buffer pixels.
   *
   * The band has to stay on a whole pixel — it is centred, so a region an odd
   * number of pixels wider than 640 would put every sprite in the game half a
   * texel off the buffer grid and undo the pixel discipline the whole renderer
   * is built around. A tile is the coarsest snap that costs nothing visible
   * (under 16 buffer pixels of letterbox at the extremes) and it keeps the
   * lattice's node spacing at 16px across a resize instead of drifting.
   */
  quantumPx: 16,
} as const;

/** Simulation runs at a fixed step; rendering interpolates between steps. */
export const SIM = {
  hz: 60,
  dt: 1 / 60,
  /** Hard cap on catch-up steps per frame so a stall cannot spiral. */
  maxStepsPerFrame: 5,
  /** Speed multipliers cycled by the fast-forward control. */
  speeds: [1, 2, 3] as const,
} as const;

/**
 * CRT composite parameters. Every value is exposed so the visual-critic loop
 * can tune against reference stills without touching shader source.
 */
export const CRT = {
  /** Aperture-grille pitch in output pixels. Below ~2.5 it aliases badly. */
  maskPitch: 3.0,
  maskStrength: 0.45,
  /** Scanline darkening at the trough, 0 = off. */
  scanlineStrength: 0.28,
  scanlineSharpness: 2.4,
  /**
   * Smallest scanline period, in output pixels.
   *
   * The scanline is one line per buffer row until a buffer row gets narrower
   * than this, and then it is one line per this many output pixels instead.
   * Without the cap the period follows the fit straight through Nyquist:
   * measured on the real fit, 1920x1080 gives 2.87 output pixels per row,
   * 1391x787 gives 2.09, and 405x870 gives 0.60. The old code answered the last
   * two by fading the scanlines out entirely, which is why two common window
   * shapes had no beam structure on screen at all.
   *
   * 2.8 rather than 3: 1920x1080 sits at 2.87, so the frame every figure in
   * reports/ was measured on keeps exactly the scanline it was measured with,
   * and every smaller fit converges on a 2.8px stripe instead of on nothing.
   */
  scanlineMinPeriod: 2.8,
  /**
   * How much of the scanline survives at fits too small to hold one per row.
   *
   * With the period pinned above, nothing here is protecting against moire any
   * more; this only thins the stripe where the picture is being *minified* and a
   * 2.8px structure is finer than anything the source carries. At 405x870 the
   * fit is 0.60 and this is the whole factor: 0.45 x 0.28 leaves a 12.6% trough,
   * which is a tube seen from across the room rather than a tube switched off.
   */
  scanlineFloor: 0.45,
  /** Barrel distortion, in normalised screen units. */
  curvatureX: 0.024,
  curvatureY: 0.034,
  /** Per-channel horizontal offset in output pixels. */
  chromaticAberration: 0.75,
  /**
   * Trail length multiplier. 1 is the tuned tube, 0 switches the pass off,
   * 2 makes every afterglow last twice as long.
   *
   * It used to be the fraction of the previous frame retained, per frame,
   * which made the trail a property of the refresh rate rather than of the
   * phosphor. The decay is now written in seconds (`phosphorTau`,
   * `phosphorRate`) and this scales the clock those constants run against.
   */
  persistence: 1.0,
  /**
   * Monomolecular decay constant per channel, in seconds. One trapped carrier
   * per site, so a fixed fraction leaves per unit time: a plain exponential.
   *
   * This is the term that *ends* the trail. The bimolecular term below falls
   * as 1/t² and would otherwise leave a ghost hanging around the denormal
   * floor for the rest of the round.
   *
   * Green longest, then red, then blue, so a white core decays through green
   * on its way out rather than fading grey. That ordering is the one the tube
   * has always used; it is now a real time constant instead of a per-frame
   * survival rate that meant nothing without knowing the refresh.
   */
  phosphorTau: [0.70, 1.00, 0.55] as const,
  /**
   * Bimolecular decay rate per channel, in inverse seconds per root-intensity.
   *
   * An electron has to find a hole before it can recombine, so the rate goes
   * as I^(3/2) and the closed form is linear in 1/sqrt(I). That single fact is
   * what makes a long-persistence tube look the way it does: a blown-out core
   * dumps most of its energy in the first few frames, then the dim remainder
   * hangs on for the best part of a second. Radar scopes are built out of this
   * (ZnS:Cu, P7 and P33); so is every very old television anyone remembers
   * smearing.
   *
   * Measured from a linear 4.0 core at these constants: down to 0.25 in about
   * 200ms, to 0.06 in 450ms, under the floor by 1.4s.
   */
  phosphorRate: [18.0, 13.0, 26.0] as const,
  /**
   * Luminance under which the trail is cut to nothing.
   *
   * Half-float denormals sit at 1e-7 forever otherwise, and a 1/t² tail is in
   * no hurry to reach them. This is the guard on the black floor: without it
   * every bright thing that has ever happened leaves a permanent smudge.
   */
  persistenceFloor: 0.005,
  /** Saturation applied to the retained trail. Above 1 pushes the hue out. */
  trailSaturation: 1.06,
  /**
   * Trail length under `prefers-reduced-motion`. A second-long ghost that
   * follows everything across the tube is motion, whatever else it is.
   * Not zero: an eighth still reads as phosphor and moves with the sprite,
   * rather than after it.
   */
  reducedPersistence: 0.12,
  /**
   * Halation: wide, warm bleed around bright areas. Reads a mid pyramid level,
   * so now that the lattice is entirely below `BLOOM.threshold` this term only
   * ever sees emitters — which makes it the cheapest way to buy the wide warm
   * halo in gwre2_deadline-bloom-skirt without touching the black floor.
   * Measured at 1.0: combat hot pixels up a fifth, empty unchanged.
   */
  halationStrength: 1.0,
  halationTint: [1.0, 0.72, 0.55] as const,
  /**
   * Light removed at the corner, as a fraction, and the shape of the falloff.
   *
   * The old shape was a product of the four edge distances raised to a quarter.
   * At 0.42 that form removed 6% at half radius and 27% at the middle of an
   * edge, then spent the rest of its depth in the last few percent of the frame,
   * so raising its strength bought a darker border and never a lens — which is
   * what a player looking at it and asking for "a vignette" was seeing. A power law on the radius puts the falloff
   * where a fast lens puts it: at 0.85 / 1.7 the corner keeps 15% of its light,
   * the middle of an edge 53%, and half radius 74%.
   *
   * 0.85 rather than 1.0, and swept by eye at 1920x1080 before it was picked:
   * at 1.0 the corner cells of the lattice go to black and the board loses
   * information a tower defense needs there. At 0.85 the corner still reads.
   *
   * It is a pure darkening, which the black floor likes and the resting frame
   * may not. Measured on `empty` at 1920x1080, playfield crop, this term alone:
   * p25 0.1037 -> 0.0886, sub-0.05 10.96% -> 17.54%, `>0.5` 9.65% -> 5.40%.
   * `reports/critique-now.md` scores our resting frame as already *darker* than
   * the Geometry Wars quiet reference (p25 0.143) and counts that against it, so
   * this is a look decision taken in the player's favour and not a free one.
   */
  vignette: 0.85,
  vignettePower: 1.7,
  /**
   * Saturation the corner gives up, as a fraction, at full vignette.
   *
   * Cheap glass loses colour along with light. Luminance-preserving by
   * construction — the blend target carries the source's own Rec.709 luma — so
   * it reads as warmth draining out of the edge rather than as a second
   * darkening, and it costs the black floor exactly nothing.
   *
   * It has to be measured through a blur to be measured at all. The aperture
   * grille below puts chroma back at full strength on every pixel (see the long
   * note at the bottom of `render/shaders/crt.ts`), so per-pixel HSV saturation
   * on the raw frame barely moves: 0.48 -> 0.46 in the outer ring. Integrated at
   * sigma 1.5, which is roughly what an eye does to a 3px stripe, the same ring
   * runs 0.416 -> 0.374 and the centre 0.434 -> 0.429. Radial, as intended.
   */
  vignetteDesat: 0.5,
  /**
   * Lomo field curvature: how far out of focus the edge of the frame runs.
   *
   * `edgeBlurStart` is a normalised radius with 1.0 at the corner, so 0.62
   * leaves the middle of every edge (0.707) barely touched and puts the softness
   * in the corners where a fast lens actually loses the field. Inside it the
   * picture is untouched — nearest taps, whole pixels, the pixel discipline the
   * rest of the renderer is built around.
   *
   * `edgeBlurPx` is the tap radius at the corner, in *output* pixels rather than
   * buffer pixels, because the defocus belongs to the glass: a fixed count of
   * buffer pixels would give a 4K window four times the blur of a phone. At the
   * 1920x1080 fit 2.2 output pixels is 0.77 of a buffer pixel, which softens a
   * sprite's edge without dissolving it.
   *
   * A blur spreads bright neighbours into dark pixels, so it is the one term
   * here that can push the black floor the wrong way. It does, and by nothing:
   * measured on `empty` at 1920x1080 with the vignette held at zero so only this
   * moves, p25 0.1037 -> 0.1042 and sub-0.05 10.96% -> 10.63%.
   *
   * Cost: four extra taps on the 27% of the frame outside the start radius, and
   * `tools/perfprobe.mjs --scenario=swarm --auto=off --post=edgeBlur:0` against
   * `edgeBlur:1` cannot see them. Three runs each, same page, same seed: median
   * 2.10 / 2.00 / 2.00 off against 1.90 / 1.90 / 2.00 on, p95 5.20 / 4.90 / 5.10
   * off against 4.90 / 3.90 / 5.30 on. The sets overlap and the cheaper median
   * is the one doing more work, which is the shape of a measurement under its
   * own noise floor rather than of a saving.
   */
  edgeBlur: 1.0,
  edgeBlurStart: 0.62,
  edgeBlurPx: 2.2,
  noise: 0.018,
  /** Slight overscan so the barrel warp never samples outside the buffer. */
  overscan: 1.008,
  /** Simulated glass reflection sheen across the top-left of the tube. */
  glare: 0.05,
} as const;

/** Multi-pass dual-filter bloom. Wide radius is what makes neon read as neon. */
export const BLOOM = {
  /** Number of downsample/upsample levels. 6 gives a very wide, soft skirt. */
  levels: 6,
  /**
   * Luminance above which a pixel contributes. Scene is HDR, so >1.
   *
   * The prefilter's knee foot is `threshold - knee`, and *below that point a
   * pixel contributes exactly zero*. At 1.15/0.55 the foot sat at 0.60, under
   * the resting lattice's 0.80, so every grid line leaked a little into the
   * pyramid — invisible at the old `intensity` 1.15 and a field-wide haze the
   * moment the emitters were given the strength they needed. 1.45/0.60 puts the
   * foot at 0.85, just clear of `GRID.baseIntensity`, which makes the black
   * floor a property of the numbers rather than of the tuning. Anything the
   * lattice does above 0.05 stress still blooms.
   */
  threshold: 1.45,
  /** Soft knee around the threshold to avoid a hard cut. */
  knee: 0.60,
  /**
   * Final additive strength, before the per-level telescoping is divided back
   * out in `PostStack.apply` — so a point emitter sees roughly `intensity /
   * 2.74` and a full-frame one sees `intensity`. At 1.15 that left a lone
   * explosion with an effective gain of 0.42 and no skirt at all: the round-one
   * emitter cut and this term compounded, and a 42-enemy fight measured 1.6
   * percentage points brighter than an empty screen. The lattice no longer
   * feeds the pyramid at all (see `threshold`), so this can carry events.
   *
   * 6.0 is the knee, measured: above about 7 the ACES wash takes over and hot
   * cores go pale without getting brighter, which shows up as a *fall* in the
   * hot-pixel count.
   */
  intensity: 6.0,
  /** Per-level weights, low index = tightest. Normalised at build time. */
  weights: [1.0, 0.85, 0.65, 0.45, 0.3, 0.2] as const,
} as const;

/** Geometry Wars deformable background lattice. */
export const GRID = {
  spacingPx: 16,
  /** Spring constant pulling a node back to rest. */
  stiffness: 26.0,
  /** Velocity damping. Too high and ripples die before they read. */
  damping: 2.1,
  /** Mass of an interior node; border nodes are pinned. */
  mass: 1.0,
  /**
   * Ripple travel cap. Must stay under half `spacingPx`, otherwise a node can
   * cross past its neighbour's rest position and invert the cell, which renders
   * as horizontal tearing rather than as elastic deformation. 0.55x spacing.
   */
  maxDisplacementPx: 9,
  lineWidthPx: 1,
  /**
   * Base emissive intensity; scales up where the lattice is stretched. At 0.42
   * against the old navy this peaked at 0.122 linear against a bloom threshold
   * of 1.0, so the grid never glowed at all and read as graph paper behind the
   * action. In every reference frame the lattice IS the stage.
   *
   * At 1.35 it overshot the other way: blue peaks at 1.0, so the *resting*
   * lattice wrote 1.35 linear and cleared `BLOOM.threshold` on every line in
   * every frame. The six-level pyramid smeared that field-wide and lifted the
   * cell interiors to 0.165 — zero pixels below 0.05 anywhere, no black left to
   * draw against. 0.80 now sits under the knee foot `threshold - knee` = 0.85,
   * so the resting lattice draws a sharp bright line and contributes *exactly*
   * zero to the pyramid. Still 6.6x round one's 0.122.
   */
  baseIntensity: 0.80,
  /**
   * Initial slope of the stress response, in emissive units per unit of
   * fractional edge strain. Read with `WarpGrid.stretchKnee`, which sets the
   * asymptote at `stretchGain / stretchKnee`.
   *
   * At 7.0 the slope was the problem, not the ceiling: a travelling ripple
   * strains an edge by 2-5%, which bought 0.14-0.35 of emissive on top of 0.80
   * and never reached the bloom threshold. Only the thin ring of edges spanning
   * the wave front — 30% strain and up — lit at all, so a blast raised the
   * measured grid line by 4% and read as *more mid-tone line area* rather than
   * as brighter line. At 26 that same 2-5% ripple writes 0.49-1.05, clears the
   * threshold along its whole length, and the field lights the way
   * gwre2_grid-shockwave-fullfield does.
   */
  stretchGain: 26.0,
  /**
   * Strain below which an edge counts as at rest. Smoothstepped in over
   * `[0, stretchFoot]`, so past the floor the response is exactly the curve
   * `stretchGain` and `stretchKnee` describe and this term disappears.
   *
   * `stretchGain` is a slope at zero, so without a floor it pays out to *every*
   * edge the moment the sheet so much as breathes. A blast leaves a few percent
   * of strain across the entire lattice behind the front, and at slope 26 that
   * lit the whole field over `BLOOM.threshold` at once. The blast frame ran
   * 66.8% of the playfield above 0.5 against grid-shockwave-fullfield's 45.3%,
   * a quiet-to-blast ratio of 8.25x against the reference's 6.0x, and the
   * corridor — 2.17x background at rest — fell to 1.13x at the peak, because
   * the background rose to meet it rather than the corridor rising. A board
   * whose one non-negotiable read is *where the enemies walk* cannot spend that.
   *
   * At 0.15 only the front and the epicentre pay. Measured against the same
   * references: blast `>0.5` 66.8% -> 47.97%, ratio 8.25x -> **5.93x** (GW
   * 5.88x); `>0.75` 19.53% -> 11.85% against GW's 11.87%; corridor-to-background
   * 1.13x -> 1.26x at the peak and 1.30x -> 1.66x by settle 20.
   *
   * Three things it does not cost, all confirmed rather than assumed. The hot
   * core holds — `>0.9` 1.17% -> 1.08%, p99 0.906 -> 0.903 — because the floor
   * gates on strain and leaves the ceiling untouched. `combat` and `swarm` are
   * unchanged to the third decimal, since neither deforms the sheet this hard.
   * And the resting frame cannot move by construction: at rest every strain is
   * under the floor whatever the floor is, which is why this knob and not
   * `BLOOM.threshold` is the one to turn. Raising the bloom threshold cuts the
   * blast too, but it dims the quiet frame along with it — measured, `empty`
   * `>0.5` 8.09% -> 4.35% at threshold 2.2 — and since the ratio is quiet *to*
   * blast, that pushes the number it was meant to fix the wrong way, to 11.1x.
   */
  stretchFoot: 0.15,
} as const;

/** Particle budget. Pools are preallocated to these sizes at boot. */
export const PARTICLES = {
  max: 24000,
  maxTrails: 1200,
  /** Points per trail ribbon. */
  trailSegments: 24,
  gravityWellRadiusPx: 140,
} as const;

/** Flocking / swarm behaviour, Geometry Wars style. */
export const FLOCK = {
  /** Neighbour search radius in pixels. Spatial hash cell matches this. */
  neighbourRadius: 26,
  separationRadius: 13,
  separationWeight: 1.9,
  alignmentWeight: 0.72,
  cohesionWeight: 0.48,
  /**
   * Pull toward the track. At 2.4 this dominated every other term and the
   * swarm rendered as a rigid extrusion of the corridor — see
   * reports/critique-motion.md P1/P4. Lowered so separation and wander can
   * actually deflect an agent off the centreline. Not all the way back to 2.4:
   * there it out-ranked `separationWeight` 1.9 and that is what produced the
   * rigid look.
   */
  pathWeight: 2.0,
  /** Max steering force applied per second. */
  maxForce: 340,
  /**
   * Wander. Previously mathematically dead: the weight lost to pathWeight by
   * 7:1, and at 0.6 Hz over a 720° sweep the vector spun fast enough to
   * average out to nothing. Stronger and much slower now, so it reads as a
   * held intention rather than as noise.
   */
  wanderWeight: 0.9,
  wanderHz: 0.35,
} as const;

/** Economy. See docs/BALANCE.md for the derivation of these numbers. */
export const ECONOMY = {
  startingCash: 1000,
  startingLives: 100,
  /** Cash granted for popping one hit point of enemy health. */
  cashPerHp: 1,
  /** End-of-round bonus: base + perRound * round. */
  roundBonusBase: 100,
  roundBonusPerRound: 1,
  /** Refund fraction when selling a tower. */
  sellRatio: 0.7,
  /** Cash multiplier if the player starts the next round early. */
  earlyStartBonusPerSecond: 2,
} as const;

/**
 * Round pacing. The *structure* those rounds are divided into lives in
 * `game/Levels.ts` — seven acts, each with its own map, slot budget and unlock
 * schedule. `count` and `Levels.TOTAL_ROUNDS` must agree, and
 * `tests/levels.test.ts` asserts it.
 */
export const WAVES = {
  count: 100,
  /** Rounds at which a boss spawns. */
  bossRounds: [20, 40, 60, 80, 100] as const,
  /** Seconds of preparation before round 1. */
  firstPrepSeconds: 20,
  prepSeconds: 6,
  /**
   * Prep window on the first round of a new act.
   *
   * Long, because an act transition refunds the whole board and changes the
   * map (`Session.beginAct`), so the player is not tuning a defence, they are
   * building one from nothing on a corridor they have never seen. Six seconds
   * is the right amount of time to buy an upgrade and the wrong amount of time
   * to lay out twenty-four towers.
   */
  actPrepSeconds: 24,
} as const;

/**
 * Quality tiers.
 *
 * Selected at boot by `core/bench.ts`, which measures a quiet frame through the
 * real post chain once per candidate tier and keeps the verdict per machine.
 * Overridable in Settings, where a picked tier becomes a ceiling the governor
 * may walk down from and the probe stops arguing with (`bench.rememberChoice`).
 */
export type QualityTier = 'potato' | 'standard' | 'ultra';

export const QUALITY: Record<QualityTier, {
  bloomLevels: number;
  particleScale: number;
  persistence: boolean;
  halation: boolean;
  gridSpacingMul: number;
  maxPixelRatio: number;
}> = {
  potato: { bloomLevels: 3, particleScale: 0.35, persistence: false, halation: false, gridSpacingMul: 2, maxPixelRatio: 1 },
  standard: { bloomLevels: 5, particleScale: 0.7, persistence: true, halation: true, gridSpacingMul: 1.5, maxPixelRatio: 1.5 },
  ultra: { bloomLevels: 6, particleScale: 1.0, persistence: true, halation: true, gridSpacingMul: 1, maxPixelRatio: 2 },
};
